import * as React from 'react';
import sub from 'date-fns/sub';
import add from 'date-fns/add';
import isFuture from 'date-fns/isFuture';
import isToday from 'date-fns/isToday';
import {
  HealthProfilePageWrapper,
  MoreOptionsCard,
  MoreOptionsLink,
} from '@leagueplatform/health-profile-common';
import { useIntl } from '@leagueplatform/locales';
import { Sidebar, SkeletonBox } from '@leagueplatform/web-common-components';
import { track } from '@leagueplatform/analytics';
import { HealthLiteracyContent } from '#components/health-literacy/health-literacy-content.tsx';
import {
  CHART_VIEW,
  CHART_VIEWS,
} from '#components/charts/constants/chart-views.ts';
import { ErrorPanel } from '#components/error-panel/index.ts';
import { HealthMetricDeserialisedDataV2 } from '@leagueplatform/dashboard-api';
import { useIsSelfReportedDataEnabled } from '#hooks/use-is-self-reported-data-enabled.hook.ts';
import { useHealthMetricDocumentTitle } from '#hooks/use-health-metric-document-title.ts';
import { useParamsLegacy as useParams } from '@leagueplatform/routing';
import { useChartData } from './hooks/use-chart-data';
import { AddMetricDataButton } from './components/add-metric-data-button';
import { HealthPrograms } from './components/health-programs';
import { HealthMetricsChart } from './components/health-metrics-chart';
import { NoDataAvailable } from './components/no-data-available';

type QueryStateHandlerProps = {
  children: (data: HealthMetricDeserialisedDataV2) => React.ReactElement;
  query: ReturnType<typeof useChartData>;
};

// Helper component to render different loading and error statues.
export function QueryStateHandler({
  children,
  query,
}: QueryStateHandlerProps): React.ReactElement {
  if (query.status === 'loading') {
    return (
      <SkeletonBox height="450px" marginTop="twoAndHalf" marginBottom="two" />
    );
  }

  if (query.status === 'error' || !query.data) {
    return (
      <ErrorPanel
        isRefetching={query.isRefetching}
        onRetry={query.refetch}
        marginBottom="two"
        error={query.error}
      />
    );
  }

  const [chartData] = query.data.data;

  const isAnyDataAvailable = !!query.data.meta?.isAnyDataAvailable;
  const canSelfRecordData = !!chartData.addDataUrl;
  // Check if any of the metric-configs returned has a device data source.
  const canConnectDevice = chartData.configs.data.some(
    (config) => config.source === 'device',
  );

  if (!isAnyDataAvailable) {
    return (
      <NoDataAvailable
        metric={chartData.metricDisplayName}
        canSelfRecordData={canSelfRecordData}
        canConnectDevice={canConnectDevice}
      />
    );
  }

  return children(query.data);
}

const handleAnalytics = ({
  metricType,
  isEmptyState,
}: {
  metricType?: string;
  isEmptyState?: boolean;
}) =>
  track('dashboard.screen_loaded.metric_item_metric_type', {
    metric_type: metricType || '',
    metric_id: '',
    is_empty_state: isEmptyState ? String(isEmptyState) : '',
  });

type Params = {
  dataType: string;
};

export const Details = () => {
  const { dataType } = useParams<Params>();
  const { isSelfReportedDataEnabled } = useIsSelfReportedDataEnabled();
  const pageViewAnalyticsRef = React.useRef(false);
  const { formatMessage } = useIntl();

  const [chartViewTab, setChartViewTab] = React.useState<CHART_VIEW>(
    CHART_VIEWS.WEEKLY,
  );
  const [chartView, setChartView] = React.useState<CHART_VIEW>(chartViewTab);
  const [metricsFrom, setMetricsFrom] = React.useState(new Date());
  const chartDataQuery = useChartData({
    chartView: chartViewTab,
    metric: dataType,
    from: metricsFrom,
  });

  const chartData = chartDataQuery?.data?.data[0];
  const meta = chartDataQuery?.data?.meta;
  const isAnyDataAvailable = !!meta?.isAnyDataAvailable;
  const metricDisplayName = chartData?.metricDisplayName;
  const hasPreviousPage = chartDataQuery.isPreviousData;
  const hasNextPage =
    chartDataQuery.isPreviousData ||
    isToday(metricsFrom) ||
    isFuture(metricsFrom);

  useHealthMetricDocumentTitle(
    formatMessage({
      id: 'METRIC_DETAILS',
    }),
    metricDisplayName,
  );

  React.useEffect(() => {
    if (metricDisplayName && !pageViewAnalyticsRef.current) {
      handleAnalytics({
        metricType: dataType,
        isEmptyState: !isAnyDataAvailable,
      });
      pageViewAnalyticsRef.current = true;
    }
  }, [dataType, metricDisplayName, isAnyDataAvailable]);

  const showAddDataButton = chartData?.addDataUrl && isSelfReportedDataEnabled;
  // When switching views we don't wont to show the skeleton boxes.  We hold on to the previous data until
  // the new set of data arrives. We also want to prevent the chart view from switching
  // until that data arrives to avoid mapping weekly data onto the monthly graph.
  // To solve this we update the view when the data has finished replacing the previous data.
  React.useEffect(() => {
    if (!chartDataQuery.isPreviousData) {
      setChartView(chartViewTab);
    }
  }, [chartDataQuery.isPreviousData, chartViewTab]);

  return (
    <HealthProfilePageWrapper
      title={metricDisplayName || formatMessage({ id: 'METRIC_DETAILS' })}
      sidebar={
        <Sidebar>
          {isAnyDataAvailable ? (
            <>
              <MoreOptionsCard>
                <MoreOptionsLink
                  to="all-recorded-data"
                  onClick={() =>
                    track(
                      'dashboard.button_clicked.metric_item_metric_type.navigate_to_all_recorded_data',
                      {
                        metric_type: dataType,
                        metric_id: '',
                        metric_status: '',
                        content_id: '',
                        content_name: '',
                      },
                    )
                  }
                >
                  {formatMessage(
                    { id: 'VIEW_ALL_METRIC_DATA' },
                    {
                      metric: metricDisplayName,
                    },
                  )}
                </MoreOptionsLink>
                <MoreOptionsLink
                  to="data-sources"
                  onClick={() =>
                    track(
                      'dashboard.button_clicked.metric_item_options_modal_metric_type.navigate_to_all_data_sources',
                      {
                        metric_type: dataType,
                        metric_id: '',
                        metric_status: '',
                        content_id: '',
                        content_name: 'view all data sources',
                        contentful_system: '',
                      },
                    )
                  }
                >
                  {formatMessage({ id: 'VIEW_DATA_SOURCES' })}
                </MoreOptionsLink>
              </MoreOptionsCard>
              {showAddDataButton ? (
                <AddMetricDataButton
                  onClick={() =>
                    track(
                      'dashboard.button_clicked.metric_item_metric_type.add_data',
                      {
                        metric_type: dataType,
                        metric_id: '',
                        is_empty_state: String(!isAnyDataAvailable),
                        content_id: '',
                        contentful_system: '',
                      },
                    )
                  }
                />
              ) : null}
            </>
          ) : null}
        </Sidebar>
      }
    >
      <QueryStateHandler query={chartDataQuery}>
        {({ data: [definedChartData] }) => (
          <HealthMetricsChart
            chartData={definedChartData}
            chartView={chartView}
            hasPreviousPage={hasPreviousPage}
            hasNextPage={hasNextPage}
            onPreviousPage={() => {
              setMetricsFrom(
                sub(metricsFrom, {
                  days: chartView === CHART_VIEWS.WEEKLY ? 7 : 30,
                }),
              );
              track('dashboard.button_clicked.metric_item_metric_type.detail', {
                metric_type: dataType,
                detail: 'backward date navigation',
                metric_id: '',
                metric_status: '',
              });
            }}
            onNextPage={() => {
              let newMetricsFrom = add(metricsFrom, {
                days: chartView === CHART_VIEWS.WEEKLY ? 7 : 30,
              });

              if (isFuture(newMetricsFrom)) {
                newMetricsFrom = new Date();
              }

              track('dashboard.button_clicked.metric_item_metric_type.detail', {
                metric_type: dataType,
                detail: 'forward date navigation',
                metric_id: '',
                metric_status: '',
              });
              setMetricsFrom(newMetricsFrom);
            }}
            onViewChange={(view) => {
              setChartViewTab(view);
              // Reset the pagination to the current day when we change views.
              setMetricsFrom(new Date());
            }}
          />
        )}
      </QueryStateHandler>
      <HealthPrograms metric={dataType} />
      <HealthLiteracyContent metric={dataType} />
    </HealthProfilePageWrapper>
  );
};
